import { useLayoutEffect, useState, type ReactNode, type RefObject } from "react";
import styled from "styled-components";

import { AnchoredPopup, type AnchoredPopupAnchor } from "../AnchoredPopup";
import {
  ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_HEIGHT,
  ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_WIDTH,
  STUDIO_BREAKPOINT,
} from "./MediaAnnotationStudioConstraint";

export interface CapturedFramePreviewPopupProps {
  /** 열린 미리보기의 frame. `null`이면 닫힌다. */
  readonly frameId: string | null;
  readonly label: string;
  /** 패널이 미디어 아래로 쌓였는지 판정할 Studio 컨테이너. */
  readonly studioRootRef: RefObject<HTMLElement | null>;
  readonly listRef: RefObject<HTMLElement | null>;
  readonly listViewportRef: RefObject<HTMLElement | null>;
  /** 현재 렌더된 행 집합. 바뀔 때 앵커 행을 다시 찾는다. */
  readonly rows: readonly { readonly frameId: string }[];
  readonly onClose: () => void;
  readonly children: ReactNode;
}

/**
 * 팝업 안에서는 카드가 자기 윤곽을 그리지 않는다 — 팝업 Panel이 border/shadow를 소유한다.
 * 높이를 주어야 이미지 영역이 남는 세로를 받아 커진다. 짧은 뷰포트에서는 팝업 clamp 여백만큼 물러난다.
 */
const PopupFrame = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: min(${ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_HEIGHT}, calc(100vh - 2rem));
  min-width: 0;
  min-height: 0;

  & > [data-captured-frame-preview-panel] {
    min-height: 0;
    border: 0;
    border-radius: 0;

    &:not([data-outside-frame-filter]) {
      background: ${({ theme }) => theme.color.bg.surfaceRaised};
    }
  }
`;

/**
 * 읽기 전용 Annotation 미리보기를 캡처 프레임 행에 붙는 팝업으로 띄운다.
 *
 * 패널의 공간이 부족하거나 사용자가 직접 분리했을 때 사용한다.
 * 팝업은 미디어 뷰포트 쪽에 서고 패널 배치는 그대로 둔다.
 *
 * 바깥 클릭으로 닫지 않는다 — 미리보기는 다른 frame을 편집하는 동안의 비교 context라, 메인
 * 뷰포트에 점을 찍거나 다른 카메라로 옮겨도 살아 있어야 한다. 닫는 길은 닫기 버튼·Escape·
 * 같은 행의 eye 토글·source 소멸이다.
 */
export function CapturedFramePreviewPopup({
  frameId,
  label,
  studioRootRef,
  listRef,
  listViewportRef,
  rows,
  onClose,
  children,
}: CapturedFramePreviewPopupProps) {
  const open = frameId !== null;
  const [stacked, setStacked] = useState(false);
  const [anchor, setAnchor] = useState<AnchoredPopupAnchor | null>(null);

  // 행이 다른 페이지로 가서 사라지면 목록 viewport에 붙어, 미리보기가 목록 곁을 떠나지 않는다.
  useLayoutEffect(() => {
    if (frameId === null) {
      setAnchor(null);
      return;
    }
    const root = studioRootRef.current;
    const update = () => {
      const row = Array.from(
        listRef.current?.querySelectorAll<HTMLElement>("[data-captured-frame-row]") ?? [],
      ).find((candidate) => candidate.dataset.frameId === frameId);
      const el = row ?? listViewportRef.current;
      setAnchor(el ? { type: "rect", rect: el.getBoundingClientRect(), el } : null);
      if (root) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        setStacked(
          root.getBoundingClientRect().width <=
            parseFloat(STUDIO_BREAKPOINT.stackedPanel) * rootFontSize,
        );
      }
    };
    update();
    // window resize 없이 소비처 패널 크기만 바뀌어도 같은 방향 안에서 앵커 위치를 갱신한다.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    if (root) observer.observe(root);
    if (listViewportRef.current) observer.observe(listViewportRef.current);
    if (listRef.current) observer.observe(listRef.current);
    return () => observer.disconnect();
  }, [frameId, studioRootRef, listRef, listViewportRef, rows]);

  return (
    <AnchoredPopup
      open={open}
      anchor={anchor}
      onClose={onClose}
      placement={stacked ? "top-start" : "left-start"}
      width={ANNOTATION_CAPTURED_FRAME_PREVIEW_POPUP_WIDTH}
      maxWidth="calc(100vw - 1rem)"
      boundary={studioRootRef.current}
      style={{ boxSizing: "border-box" }}
      closeOnOutsidePointerDown={false}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- portal 이라 탭 순서상 문서 끝에 있어, 연 즉시 들어가야 키보드로 닿는다
      autoFocus="panel"
      returnFocus
      role="dialog"
      aria-label={label}
    >
      <PopupFrame data-annotation-reference-preview="" data-preview-layout="popup">
        {children}
      </PopupFrame>
    </AnchoredPopup>
  );
}
